// Merkezi validasyon (zod) — el yazması kuralların tek kaynağı.
// Mevcut `validateCustomer/validateItems/validateContact` davranışları korunur;
// yeni kod parse* fonksiyonlarını kullanır ({ ok, value } dönüşü, `!` gerektirmez).

import { z } from "zod";
import type { OrderCustomer, VerifiedOrderItem } from "@/lib/orders";
import { PHONE_RE } from "@/lib/phone";

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Telefon kuralı tek kaynağı: `@/lib/phone` (Y14: en az 7 rakam şartı).
export { PHONE_RE };
export const POSTAL_RE = /^\d{5}$/;
export const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,80}$/;

export const TURKISH_CITIES = [
  "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Aksaray", "Amasya", "Ankara", "Antalya",
  "Ardahan", "Artvin", "Aydın", "Balıkesir", "Bartın", "Batman", "Bayburt", "Bilecik",
  "Bingöl", "Bitlis", "Bolu", "Burdur", "Bursa", "Çanakkale", "Çankırı", "Çorum",
  "Denizli", "Diyarbakır", "Düzce", "Edirne", "Elazığ", "Erzincan", "Erzurum", "Eskişehir",
  "Gaziantep", "Giresun", "Gümüşhane", "Hakkari", "Hatay", "Iğdır", "Isparta", "İstanbul",
  "İzmir", "Kahramanmaraş", "Karabük", "Karaman", "Kars", "Kastamonu", "Kayseri", "Kırıkkale",
  "Kırklareli", "Kırşehir", "Kilis", "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa",
  "Mardin", "Mersin", "Muğla", "Muş", "Nevşehir", "Niğde", "Ordu", "Osmaniye",
  "Rize", "Sakarya", "Samsun", "Siirt", "Sinop", "Sivas", "Şanlıurfa", "Şırnak",
  "Tekirdağ", "Tokat", "Trabzon", "Tunceli", "Uşak", "Van", "Yalova", "Yozgat",
  "Zonguldak",
];

export const MAX_QTY = 99;
export const MAX_LINES = 50;

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function firstIssue(error: z.ZodError): string {
  return error.issues[0]?.message || "Geçersiz bilgi";
}

/** Fatura ham girdisi — tanımsız/boşsa undefined (şema atlar), bozuksa ham obje (şema reddeder). */
function parseInvoice(input: unknown): Record<string, unknown> | undefined {
  if (input === undefined || input === null) return undefined;
  const o = input as Record<string, unknown>;
  const hasAny = ["name", "address", "city", "kind", "taxNo", "taxOffice"].some(
    (k) => typeof o[k] === "string" && (o[k] as string).trim() !== ""
  );
  if (!hasAny) return undefined;
  return {
    name: str(o.name),
    address: str(o.address),
    city: str(o.city),
    kind: o.kind === "kurumsal" ? "kurumsal" : "bireysel",
    taxNo: str(o.taxNo),
    taxOffice: str(o.taxOffice),
  };
}

// --- Müşteri (checkout) ---

const customerSchema = z.object({
  name: z.string().trim().min(3, "Ad Soyad 3-100 karakter olmalı").max(100, "Ad Soyad 3-100 karakter olmalı"),
  email: z.string().trim().max(254, "Geçersiz e-posta").refine((v) => EMAIL_RE.test(v), "Geçersiz e-posta"),
  phone: z.string().trim().refine((v) => PHONE_RE.test(v), "Geçersiz telefon"),
  address: z.string().trim().min(10, "Adres 10-500 karakter olmalı").max(500, "Adres 10-500 karakter olmalı"),
  city: z.string().trim().refine((v) => TURKISH_CITIES.includes(v), "Geçerli bir şehir seçin"),
  postal: z.string().trim().refine((v) => !v || POSTAL_RE.test(v), "Posta kodu 5 haneli olmalı"),
  kvkk: z.boolean().refine((v) => v, "KVKK onayı gerekli"),
  contract: z.boolean().refine((v) => v, "Mesafeli satış sözleşmesi onayı gerekli"),
  preinfo: z.boolean().refine((v) => v, "Ön bilgilendirme onayı gerekli"),
  note: z.string().trim().max(500).optional(),
  whatsappOptIn: z.boolean().optional(),
  // Fatura adresi — kapalıysa yok; açıksa teslimat kurallarıyla aynı katılık
  invoice: z
    .object({
      name: z.string().trim().min(3, "Fatura adı 3-120 karakter olmalı").max(120),
      address: z.string().trim().min(10, "Fatura adresi 10-500 karakter olmalı").max(500),
      city: z.string().trim().refine((v) => TURKISH_CITIES.includes(v), "Geçerli bir fatura şehri seçin"),
      kind: z.enum(["bireysel", "kurumsal"]),
      taxNo: z
        .string()
        .trim()
        .refine((v) => !v || /^\d{10,11}$/.test(v), "Vergi/TC no 10-11 haneli olmalı"),
      taxOffice: z.string().trim().max(80).optional(),
    })
    .refine(
      (v) => v.kind === "bireysel" || (v.taxNo.length === 10 && (v.taxOffice?.trim().length ?? 0) >= 2),
      "Kurumsal faturada 10 haneli vergi no + vergi dairesi gerekli"
    )
    .optional(),
});

export type ParsedInvoice = {
  name: string;
  address: string;
  city: string;
  kind: "bireysel" | "kurumsal";
  taxNo: string;
  taxOffice?: string;
};

export type ParsedCustomer = OrderCustomer & {
  kvkk: true;
  contract: true;
  preinfo: true;
  note?: string;
  invoice?: ParsedInvoice;
};

export function parseCustomer(input: unknown): { ok: true; value: ParsedCustomer } | { ok: false; error: string } {
  const o = (input ?? {}) as Record<string, unknown>;
  const r = customerSchema.safeParse({
    name: str(o.name),
    email: str(o.email),
    phone: str(o.phone),
    address: str(o.address),
    city: str(o.city),
    postal: str(o.postal),
    kvkk: o.kvkk === true,
    contract: o.contract === true,
    preinfo: o.preinfo === true,
    note: typeof o.note === "string" && o.note.trim() ? o.note.trim().slice(0, 500) : undefined,
    whatsappOptIn: typeof o.whatsappOptIn === "boolean" ? o.whatsappOptIn : undefined,
    invoice: parseInvoice(o.invoice),
  });
  if (!r.success) return { ok: false, error: firstIssue(r.error) };
  return { ok: true, value: r.data as ParsedCustomer };
}

// --- Sepet satırları (checkout) ---

const checkoutItemSchema = z
  .object({
    // A5: carts.ts ile aynı katılık (SLUG_RE) — boş slug + isim fallback'i korunur
    slug: z.string().trim().toLowerCase().max(80).refine((v) => !v || SLUG_RE.test(v), "Geçersiz ürün"),
    name: z.string().trim().max(200),
    quantity: z.number(),
    variant: z.string().trim().max(40).optional(),
  })
  .refine((it) => it.slug || it.name, "Geçersiz ürün")
  .refine((it) => Number.isInteger(it.quantity) && it.quantity >= 1 && it.quantity <= MAX_QTY, "Geçersiz adet (1-99)");

export type ParsedCheckoutItem = { slug: string; name: string; quantity: number; variant?: string };

export function parseCheckoutItems(input: unknown): { ok: true; value: ParsedCheckoutItem[] } | { ok: false; error: string } {
  if (!Array.isArray(input) || input.length === 0) return { ok: false, error: "Sepet boş" };
  if (input.length > MAX_LINES) return { ok: false, error: "Sepet çok büyük (en fazla 50 satır)" };
  const value: ParsedCheckoutItem[] = [];
  for (const raw of input) {
    const o = (raw ?? {}) as Record<string, unknown>;
    const qty = Math.floor(Number(o.quantity));
    const r = checkoutItemSchema.safeParse({
      slug: str(o.slug),
      name: str(o.name),
      quantity: Number.isFinite(qty) ? qty : 0,
      variant: typeof o.variant === "string" && o.variant.trim() ? o.variant.trim().slice(0, 40) : undefined,
    });
    if (!r.success) return { ok: false, error: firstIssue(r.error) };
    value.push({ slug: r.data.slug, name: r.data.name, quantity: r.data.quantity, ...(r.data.variant ? { variant: r.data.variant } : {}) });
  }
  return { ok: true, value };
}

// --- Adres defteri ---

const addressSchema = z.object({
  label: z.string().trim().max(120).optional(),
  fullName: z.string().trim().min(2, "Ad Soyad en az 2 karakter olmalı").max(100),
  phone: z.string().trim().refine((v) => PHONE_RE.test(v), "Geçerli bir telefon girin"),
  address: z.string().trim().min(10, "Adres en az 10 karakter olmalı").max(500),
  city: z.string().trim().refine((v) => TURKISH_CITIES.includes(v), "Geçerli bir şehir seçin"),
  postal: z.string().trim().refine((v) => !v || POSTAL_RE.test(v), "Posta kodu 5 haneli olmalı"),
  isDefault: z.boolean().optional(),
});

export type ParsedAddress = {
  label?: string;
  fullName: string;
  phone: string;
  address: string;
  city: string;
  postal?: string;
  isDefault?: boolean;
};

export function parseAddress(input: unknown): { ok: true; value: ParsedAddress } | { ok: false; error: string } {
  const o = (input ?? {}) as Record<string, unknown>;
  const r = addressSchema.safeParse({
    label: typeof o.label === "string" && o.label.trim() ? o.label.trim().slice(0, 120) : undefined,
    fullName: str(o.fullName),
    phone: str(o.phone),
    address: str(o.address),
    city: str(o.city),
    postal: str(o.postal),
    isDefault: o.isDefault === true ? true : undefined,
  });
  if (!r.success) return { ok: false, error: firstIssue(r.error) };
  const v = r.data;
  return {
    ok: true,
    value: {
      ...(v.label ? { label: v.label } : {}),
      fullName: v.fullName,
      phone: v.phone,
      address: v.address,
      city: v.city,
      ...(v.postal ? { postal: v.postal } : {}),
      ...(v.isDefault !== undefined ? { isDefault: v.isDefault } : {}),
    },
  };
}

/** Kısmi adres güncellemesi (PATCH) — verilen alanlar doğrulanır. */
export function parseAddressPatch(input: unknown): { ok: true; value: Partial<ParsedAddress> } | { ok: false; error: string } {
  const o = (input ?? {}) as Record<string, unknown>;
  if (o.isDefault !== undefined && typeof o.isDefault !== "boolean") {
    return { ok: false, error: "Geçersiz istek" };
  }
  const shaped: Record<string, unknown> = {};
  if (o.label !== undefined) shaped.label = typeof o.label === "string" && o.label.trim() ? o.label.trim().slice(0, 120) : "";
  if (o.fullName !== undefined) shaped.fullName = str(o.fullName);
  if (o.phone !== undefined) shaped.phone = str(o.phone);
  if (o.address !== undefined) shaped.address = str(o.address);
  if (o.city !== undefined) shaped.city = str(o.city);
  if (o.postal !== undefined) shaped.postal = str(o.postal);
  if (o.isDefault !== undefined) shaped.isDefault = o.isDefault;
  if (Object.keys(shaped).length === 0) return { ok: false, error: "Güncellenecek alan yok" };
  const r = addressSchema.partial().safeParse(shaped);
  if (!r.success) return { ok: false, error: firstIssue(r.error) };
  const v = r.data;
  return {
    ok: true,
    value: {
      // label/postal boş string gelebilir — route "" → null (temizleme) yapar
      ...(v.label !== undefined ? { label: v.label } : {}),
      ...(v.fullName !== undefined ? { fullName: v.fullName } : {}),
      ...(v.phone !== undefined ? { phone: v.phone } : {}),
      ...(v.address !== undefined ? { address: v.address } : {}),
      ...(v.city !== undefined ? { city: v.city } : {}),
      ...(v.postal !== undefined ? { postal: v.postal } : {}),
      ...(v.isDefault !== undefined ? { isDefault: v.isDefault } : {}),
    },
  };
}

// --- Kayıtlı veri ayrıştırma (Prisma Json sınırı — asla throw etmez) ---

/** Bozuk satırı sanitize eder: string→"", sayı→0, dizi→temiz liste. */
export function parseStoredCustomer(raw: unknown): { value: OrderCustomer; corrupt: boolean } {
  const o = (raw ?? {}) as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : "");
  const value: OrderCustomer = {
    name: pick(o.name),
    email: pick(o.email),
    phone: pick(o.phone),
    address: pick(o.address),
    city: pick(o.city),
    ...(typeof o.postal === "string" && o.postal ? { postal: o.postal } : {}),
    ...(typeof o.whatsappOptIn === "boolean" ? { whatsappOptIn: o.whatsappOptIn } : {}),
  };
  const corrupt =
    !value.name || !EMAIL_RE.test(value.email) || !value.phone || !value.address || !value.city;
  return { value, corrupt };
}

export function parseStoredItems(raw: unknown): { value: VerifiedOrderItem[]; corrupt: boolean } {
  if (!Array.isArray(raw)) return { value: [], corrupt: true };
  const value: VerifiedOrderItem[] = [];
  let corrupt = false;
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) {
      corrupt = true;
      continue;
    }
    const r = entry as Record<string, unknown>;
    const slug = typeof r.slug === "string" ? r.slug : "";
    const name = typeof r.name === "string" ? r.name : "";
    const price = Number(r.price);
    const quantity = Math.floor(Number(r.quantity));
    if (!slug || !name || !Number.isFinite(price) || !Number.isFinite(quantity) || quantity < 1) {
      corrupt = true;
      continue;
    }
    value.push({
      slug,
      name,
      price,
      quantity,
      ...(typeof r.image === "string" ? { image: r.image } : {}),
      ...(typeof r.variant === "string" ? { variant: r.variant } : {}),
    });
  }
  return { value, corrupt };
}
